import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import CustomTooltip from "../../../ui/CustomTooltip";
import {
  INTERLOCKED,
  generatorIsOn,
  isTekFish,
  outputsAreInconsistent,
  resolveControlMode,
  resolveRunOrder,
} from "../../../utils/generatorOutputs";
import "./Generators.css";

const text = (translate, key, fallback) => {
  const value = typeof translate === "function" ? translate(key) : undefined;
  return value && value !== key ? value : fallback;
};

function resolveTekFish(item, translate) {
  const statuses = Array.isArray(item?.connection_status) ? item.connection_status : [];
  const commOk = statuses.includes(true);

  if (!commOk) {
    return {
      message: text(translate, "noComm", "Sin comunicacion"),
      imgSrc: "yellow",
      alt: "no-comm",
    };
  }

  if (item?.falla_generador_tekfish) {
    return {
      message: text(translate, "alerta_generador", "Alarma en Generador"),
      imgSrc: "red",
      alt: "alarm",
    };
  }

  switch (item?.digital_outputs_state) {
    case "10":
      return {
        message: text(translate, "Señal_on_enviada", "Senal de Encendido Enviada"),
        imgSrc: "green",
        alt: "on-signal",
      };
    case "01":
      return {
        message: text(translate, "Señal_off_enviada", "Senal de Apagado Enviada"),
        imgSrc: "gray",
        alt: "off",
      };
    default:
      return {
        message: text(translate, "unknown_status", "Estado desconocido"),
        imgSrc: "gray",
        alt: "unknown",
      };
  }
}

function defaultResolve(item, translate) {
  if (isTekFish(item)) {
    return resolveTekFish(item, translate);
  }

  const state = String(item?.STATE || "").toUpperCase();
  const connectionStatus = Array.isArray(item?.connection_status) ? item.connection_status : [];
  const commOk = connectionStatus.includes(true);

  if (!commOk) {
    return { message: text(translate, "noComm", "Sin comunicacion"), imgSrc: "yellow", alt: "no-comm" };
  }

  const outsOn = generatorIsOn(item);
  const order = resolveRunOrder(item);

  // La inconsistencia de salidas solo se declara con control por falla false
  // explicito. El resto sale del cruce entre orden de marcha y salidas, que no
  // depende de enumerar patrones validos (110, 011, 101, ...).
  const outsBad =
    outputsAreInconsistent(item?.digital_outputs_state, item?.control_generator_back) ||
    (order !== null && outsOn !== null && order !== outsOn);

  if (outsBad) {
    return { message: text(translate, "mismatch", "Inconsistencia de salidas"), imgSrc: "red", alt: "mismatch" };
  }

  if (state === "HOLD_BEFORE_OFF") {
    return { message: text(translate, "shuttingDown", "Apagando..."), imgSrc: "teal", alt: "hold-before-off" };
  }

  if (state === "WARMUP") {
    return { message: text(translate, "warming", "Calentando"), imgSrc: "orange", alt: "warming" };
  }

  if (state === "IS_ON" && outsOn === true) {
    return { message: text(translate, "Señal_on_enviada", "Senal de Encendido Enviada"), imgSrc: "green", alt: "on" };
  }

  if (order === false && outsOn === false) {
    return { message: text(translate, "Señal_off_enviada", "Senal de Apagado Enviada"), imgSrc: "gray", alt: "off" };
  }

  return { message: text(translate, "idle", "En espera"), imgSrc: "gray", alt: "idle" };
}

export function resolveGenerator(item, translate) {
  if (!item || typeof item !== "object") {
    return { message: text(translate, "invalid_data", "Datos no validos"), imgSrc: "gray", alt: "invalid" };
  }

  if ("plc_status" in item) {
    if (item.plc_status === 100) {
      return { message: text(translate, "alarma_generador", "Alarma en Generador"), imgSrc: "red", alt: "alarm" };
    }

    if (item.plc_status === -1) {
      return {
        message: text(translate, "sin_comunicacion_generador", "Sin comunicacion con Generador"),
        imgSrc: "yellow",
        alt: "no-comm",
      };
    }

    if (item.partida === 100 && item.detencion === 60) {
      return { message: text(translate, "Señal_on_enviada", "Senal de Encendido Enviada"), imgSrc: "green", alt: "on" };
    }

    if (item.partida === 60 && item.detencion === 100) {
      return { message: text(translate, "Señal_off_enviada", "Senal de Apagado Enviada"), imgSrc: "gray", alt: "off" };
    }

    if (item.partida === 120 && item.detencion === 60) {
      return { message: text(translate, "generator_on_manual", "Generador Encendido Manualmente"), imgSrc: "green", alt: "manual" };
    }

    return {
      message: text(translate, "sin_informacion_generador", "Sin Informacion Generador"),
      imgSrc: "gray",
      alt: "off",
    };
  }

  if ("hf_status" in item) {
    if (item.hf_status === -2) {
      return {
        message: text(translate, "sin_comunicacion_generador", "Sin comunicacion con Generador"),
        imgSrc: "yellow",
        alt: "no-comm",
      };
    }

    if (item.hf_status === 100) {
      return { message: text(translate, "Señal_on_enviada", "Senal de Encendido Enviada"), imgSrc: "green", alt: "on" };
    }

    if (item.hf_status === 80) {
      return { message: `${text(translate, "off_generator", "Apagando Generador")}...`, imgSrc: "teal", alt: "hold-before-off" };
    }

    if (item.hf_status === 60) {
      return { message: text(translate, "Señal_off_enviada", "Senal de Apagado Enviada"), imgSrc: "gray", alt: "off" };
    }

    if (item.hf_status === 120) {
      return { message: text(translate, "generator_on_manual", "Generador Encendido Manualmente"), imgSrc: "green", alt: "manual" };
    }

    return {
      message: `${text(translate, "status", "Estado")} HF ${item.hf_status}`,
      imgSrc: "yellow",
      alt: "unknown",
    };
  }

  return defaultResolve(item, translate);
}

function isFsmGenerator(item) {
  return (
    Boolean(item) &&
    typeof item === "object" &&
    !isTekFish(item) &&
    !("plc_status" in item) &&
    !("hf_status" in item) &&
    ("digital_outputs_state" in item || "control_generator_back" in item)
  );
}

function controlBackLabel(item, translate) {
  const { modo, reportado } = resolveControlMode(item?.control_generator_back);

  if (!reportado) {
    // El campo no llego. Se opera como inactivo, pero no es un valor reportado
    // y no debe presentarse como si el controlador lo hubiera confirmado.
    return text(translate, "control_falla_no_reportado", "Inactivo (no reportado)");
  }

  return modo === INTERLOCKED
    ? text(translate, "control_falla_activo", "Activo (Q1/Q3 excluyentes)")
    : text(translate, "control_falla_inactivo", "Inactivo");
}

function DetailsTooltip({ id_generador, item, translate, info }) {
  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
        {text(translate, "generator", "Generador")} {id_generador + 1}
      </Typography>

      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {info}
      </Typography>

      <Typography variant="caption" component="div">
        {"communication_status" in item && (
          <>
            <strong>{text(translate, "Comunicacion", "Comunicacion")}:</strong>{" "}
            {item.communication_status ? "OK" : text(translate, "falla", "Falla")}
          </>
        )}
      </Typography>

      {isFsmGenerator(item) && (
        <Typography variant="caption" component="div">
          <strong>{text(translate, "control_falla", "Control por falla")}:</strong>{" "}
          {controlBackLabel(item, translate)}
          {item.digital_outputs_state ? ` · ${item.digital_outputs_state}` : ""}
        </Typography>
      )}
    </Box>
  );
}

DetailsTooltip.propTypes = { item: PropTypes.object.isRequired };

export function GeneratorsStatus({
  items,
  sizeVh = 5,
  gap = 16,
  resolveVisual = resolveGenerator,
  showMessage = true,
  translate,
  wrap = false,
  compact = false,
  onSelectGenerator
}) {
  let normalized = [];

  if (Array.isArray(items)) {
    if (items.every((entry) => Array.isArray(entry) && entry.length === 2)) {
      normalized = items;
    } else {
      normalized = items.map((payload, index) => [`gen${index + 1}`, payload]);
    }
  } else if (items && typeof items === "object") {
    const keys = Object.keys(items);
    if (keys.length > 1 && !("hf_status" in items) && !("plc_status" in items)) {
      normalized = Object.entries(items);
    } else {
      normalized = [["gen1", items]];
    }
  }

  if (!normalized.length) {
    console.warn("No hay generadores validos para mostrar:", items);
    return null;
  }

  const resolvedGap = typeof gap === "number" ? `${gap}px` : gap;

  return (
    <Box  sx={{ width: "100%", overflowX: "auto"  }}    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          gap: resolvedGap,
        }}
      >
        {normalized
          .filter(([, payload]) => payload && typeof payload === "object")
          .map(([id, payload], index) => {
            const visual = resolveVisual(payload, translate);

            return (
              <CustomTooltip
                key={`${id}-${index}`}
                variant="pipeline"
                title={<DetailsTooltip title={id} id_generador={index} item={payload} translate={translate} info={visual.message} />}
                color="#c3c3c3"
                arrow
              >
                <Box
                  onClick={() => {
                    onSelectGenerator?.(id);
                  }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    minWidth: `${sizeVh * 1.2}vh`,
                    maxWidth:  "none",
                    overflow: "hidden",
                  }}
                >
                  <IconoGenerador color={COLORS[visual.imgSrc]} />
                </Box>
              </CustomTooltip>
            );
          })}
      </Box>
    </Box>
  );
}

const COLORS = {
  green: "#296f4d",
  yellow: "#d7c81d",
  gray: "#747474",
  red: "#7f1d1d",
  orange: "#d7592a",
  teal: "#296f6f",
};

function IconoGenerador({ color }) {
  return (
    <div className="icono-generador-container-dash">
      <svg viewBox="0 0 220 140" className="icono-generador-svg-dash" aria-hidden="true">
        <polygon points="100,65 160,65 130,15" fill="#0c1c2d" />

        <g
          stroke={color}
          fill="none"
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
          transform="translate(30, 8)"
        >
          <rect x="20" y="35" width="120" height="70" rx="8" ry="8" />
          <rect x="10" y="50" width="8" height="40" rx="4" ry="4" />
          <rect x="40" y="50" width="35" height="4" />
          <line x1="40" y1="65" x2="70" y2="65" />
          <line x1="40" y1="75" x2="70" y2="75" />
          <line x1="40" y1="85" x2="70" y2="85" />
          <circle cx="90" cy="52" r="3" />
          <line x1="20" y1="125" x2="150" y2="125" />
          <rect x="40" y="105" width="14" height="14" rx="4" ry="4" />
          <rect x="110" y="105" width="14" height="14" rx="4" ry="4" />
          <polygon points="100,65 160,65 130,15" className="icono-triangulo" />
          <path d="M131 32 L125 45 L133 45 L127 57" />
        </g>
      </svg>
    </div>
  );
}
